import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { skipToken } from '@reduxjs/toolkit/query';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { Icon, spaces, Tooltip } from '@electricjs/arc';
import {
  EmployeeLeaveStatusEnum,
  EmployeeStatusEnum,
} from '@electricjs/core_entity-client';

import {
  useGetEmployeeFromDirectoryQuery,
  useGetEmployeeLeavesQuery,
} from '@/redux/slices/employeeApiSlice';
import { useGetOrganizationId } from '@/hooks/useGetOrganizationId';
import formatDate from '@/helpers/formatDate';
import { EmployeeLeave } from '@/types/employees';

type EmployeeOnLeaveIconProps = {
  employeeId?: string;
  /**
   * Pass this when the caller already knows the employee's status (e.g. from
   * a row that already fetched it) to skip this component's own directory
   * lookup, which otherwise exists solely to hide the icon for offboarded
   * employees (an employee can't be "on leave" once inactive).
   */
  employeeStatus?: EmployeeStatusEnum;
  /**
   * Pass this when the caller already has the employee's leaves (e.g. from a
   * batched lookup) to skip this component's own per-employee fetch.
   */
  leaves?: EmployeeLeave[];
  /**
   * Pass this when the caller is fetching `leaves` in bulk but that request
   * hasn't resolved yet. Without it, `leaves === undefined` during the bulk
   * fetch's loading window is indistinguishable from "no batched lookup was
   * provided at all", so this component would both fall back to its own
   * per-employee fetch (defeating the batching) and, once that flag is
   * cleared some other way, briefly render "not on leave" before the real
   * data arrives.
   */
  leavesLoading?: boolean;
};

const EmployeeOnLeaveIcon = ({
  employeeId,
  employeeStatus,
  leaves,
  leavesLoading,
}: EmployeeOnLeaveIconProps) => {
  const { t } = useTranslation('people');
  const { ithEmployeeLeave } = useFlags();
  const organizationId = useGetOrganizationId();
  const { data: fullEmployee } = useGetEmployeeFromDirectoryQuery(
    ithEmployeeLeave && employeeId && employeeStatus === undefined
      ? { employeeId, organizationId }
      : skipToken
  );
  // undefined here means the status hasn't resolved yet (directory lookup
  // still loading, or it failed) - not that the employee is active - so
  // callers relying on this lookup (Assets, Devices) must treat it as
  // unknown rather than assuming it's safe to show the icon.
  const resolvedStatus = employeeStatus ?? fullEmployee?.status;
  const isStatusUnresolved =
    employeeStatus === undefined && resolvedStatus === undefined;

  const { data: employeeLeaveData, isError } = useGetEmployeeLeavesQuery(
    ithEmployeeLeave && employeeId && leaves === undefined && !leavesLoading
      ? {
          organizationId,
          employeeId,
        }
      : skipToken
  );

  useEffect(() => {
    if (isError) {
      window?.DD_RUM?.addError(
        `Failed to fetch leave status for employee ${employeeId}`,
        { location: 'EmployeeOnLeaveIcon' }
      );
    }
  }, [isError, employeeId]);

  const resolvedLeaves = leaves ?? employeeLeaveData?.results;
  const leave =
    resolvedLeaves?.find(
      l => l.status === EmployeeLeaveStatusEnum.InProgress
    ) ??
    resolvedLeaves?.find(l => l.status === EmployeeLeaveStatusEnum.Scheduled);
  const isUpcoming = leave?.status === EmployeeLeaveStatusEnum.Scheduled;

  if (
    !ithEmployeeLeave ||
    leavesLoading ||
    !leave ||
    isStatusUnresolved ||
    resolvedStatus === EmployeeStatusEnum.Inactive
  ) {
    return null;
  }

  const dateFormatOptions: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  };

  // Deliberately not dimmed when rendered inside a disabled select option
  // (e.g. champion/assignee pickers): Arc's Select sets `pointer-events:
  // none` on disabled options, so this Tooltip can never actually be
  // triggered there - this icon ends up being the only visible signal for
  // *why* the option is disabled, so it should stay in its default color
  // rather than fade along with the greyed-out option text.
  return (
    <Tooltip
      content={
        isUpcoming
          ? leave.returnDate
            ? t('employeeOnLeaveIcon.upcomingDateRangeWithReturn', {
                startDate: formatDate(leave.startDate, dateFormatOptions),
                returnDate: formatDate(leave.returnDate, dateFormatOptions),
              })
            : t('employeeOnLeaveIcon.upcomingDateRangeNoReturn', {
                startDate: formatDate(leave.startDate, dateFormatOptions),
              })
          : leave.returnDate
            ? t('employeeOnLeaveIcon.dateRangeWithReturn', {
                startDate: formatDate(leave.startDate, dateFormatOptions),
                returnDate: formatDate(leave.returnDate, dateFormatOptions),
              })
            : t('employeeOnLeaveIcon.dateRangeNoReturn', {
                startDate: formatDate(leave.startDate, dateFormatOptions),
              })
      }
      placement="top">
      <Icon icon="calendar" size="small" mr={spaces.space_xs} />
    </Tooltip>
  );
};

export default EmployeeOnLeaveIcon;
